import React from 'react';
import PageWrapper from '../components/PageWrapper';

export const AfricaOps = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero">
      <p className="section-label">Operations</p>
      <h1>Africa Operations</h1>
      <p>On-the-ground presence across 18 African nations and growing.</p>
    </div>
    <section className="section" style={{ background: '#071a12' }}>
      <div className="container">
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          {[
            {
              region: 'West Africa',
              countries: [
                ['Ghana', 'Mining, gold extraction, renewable energy projects', 'HUB'],
                ['Nigeria', 'Energy sector advisory, investment facilitation', 'Active'],
                ['Sierra Leone', 'Diamond sourcing, artisanal mining support', 'Active'],
                ['Guinea', 'Bauxite and iron ore project development', 'Active'],
                ['Côte d\'Ivoire', 'Cocoa sector advisory, renewable energy', 'Active'],
              ]
            },
            {
              region: 'East Africa',
              countries: [
                ['Kenya', 'Eco-tourism, renewable energy, infrastructure', 'HUB'],
                ['Tanzania', 'Tanzanite trade, wildlife tourism, solar energy', 'Active'],
                ['Uganda', 'Hydro power projects, eco-tourism development', 'Active'],
                ['Mozambique', 'LNG advisory, renewable energy, eco-tourism', 'Active'],
                ['Ethiopia', 'Hydro and wind energy, agricultural advisory', 'Active'],
              ]
            },
            {
              region: 'Southern Africa',
              countries: [
                ['Botswana', 'Diamond trade, safari investment facilitation', 'HUB'],
                ['Zimbabwe', 'Mining consultancy, diamond field operations', 'Active'],
                ['Zambia', 'Copper belt mining advisory, energy projects', 'Active'],
                ['Angola', 'Diamond export facilitation, oil sector advisory', 'Active'],
                ['Namibia', 'Diamond mining, eco-tourism, solar energy', 'Active'],
              ]
            },
          ].map(({ region, countries }) => (
            <div key={region} style={{ marginBottom: '2.5rem' }}>
              <h3 style={{ color: '#c9a84c', fontSize: '.78rem', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '1rem', fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>{region}</h3>
              {countries.map(([country, desc, status]) => (
                <div key={country} className="country-row">
                  <div><h4>{country}</h4><p>{desc}</p></div>
                  <span className="country-badge" style={{ background: status === 'HUB' ? '#c9a84c' : 'rgba(201,168,76,0.2)', color: status === 'HUB' ? '#071a12' : '#c9a84c' }}>{status}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Projects = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero">
      <p className="section-label">Portfolio</p>
      <h1>Our Projects</h1>
      <p>A selection of landmark engagements across Africa.</p>
    </div>
    <section className="section">
      <div className="container">
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {[
            { country: 'Ghana · Mining', title: 'Ashanti Gold Belt Feasibility Study', desc: 'Comprehensive geological and financial feasibility study for a mid-tier gold mining company entering Ghana\'s Ashanti belt. Delivered resource estimates, regulatory roadmap, and community engagement framework.', status: 'Completed 2023' },
            { country: 'Kenya · Renewable Energy', title: 'Lake Turkana Wind Farm Advisory', desc: 'Strategic advisory and regulatory navigation for a 310MW wind energy project in northern Kenya, facilitating $860M in infrastructure investment and grid connection agreements.', status: 'Completed 2022' },
            { country: 'Botswana · Diamonds', title: 'Orapa Mine Trade Facilitation', desc: 'Established export channels and international buyer relationships for rough diamond production, achieving 18% premium over previous pricing benchmarks.', status: 'Ongoing' },
            { country: 'Tanzania · Eco Tourism', title: 'Serengeti Luxury Lodge Development', desc: 'End-to-end development consultancy for a 40-suite conservation lodge, including investment structuring, environmental permits, community revenue sharing, and international operator partnerships.', status: 'Completed 2024' },
            { country: 'Zimbabwe · Mining', title: 'Marange Diamond Field Operations Review', desc: 'Comprehensive operational audit and compliance review for a major diamond producer in Zimbabwe\'s Marange field, covering Kimberley Process requirements and international market access.', status: 'Completed 2023' },
            { country: 'Mozambique · Renewable Energy', title: 'Northern Mozambique Solar Initiative', desc: 'Feasibility and project development for a 150MW solar PV plant serving northern Mozambique\'s growing industrial and residential demand.', status: 'In Progress' },
            { country: 'Uganda · Eco Tourism', title: 'Bwindi Gorilla Tourism Development', desc: 'Community-based eco-tourism framework development adjacent to Bwindi Impenetrable Forest, creating sustainable livelihoods for 3,000+ households.', status: 'Completed 2022' },
          ].map(p => (
            <div key={p.title} className="project-card">
              <div className="project-country">{p.country}</div>
              <h4>{p.title}</h4>
              <p>{p.desc}</p>
              <span className="project-status" style={{ background: p.status === 'Ongoing' || p.status === 'In Progress' ? 'rgba(201,168,76,0.15)' : 'rgba(13,61,42,0.08)', color: p.status === 'Ongoing' || p.status === 'In Progress' ? '#c9a84c' : 'var(--green-dark)' }}>{p.status}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Team = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero">
      <p className="section-label">Leadership</p>
      <h1>Our Team</h1>
      <p>Seasoned experts united by a passion for African development.</p>
    </div>
    <section className="section">
      <div className="container">
        <div className="grid-4">
          {[
            { initials: 'RK', name: 'Rajesh Kumar', role: 'CEO & Founder', desc: '25+ years in African resource markets. Former MD at a leading commodities firm with deep networks across 18 nations.' },
            { initials: 'AP', name: 'Amara Patel', role: 'Director, Mining', desc: 'Geologist with 20 years of experience across West and Southern Africa\'s mineral-rich regions.' },
            { initials: 'JO', name: 'James Okafor', role: 'Head, Africa Operations', desc: 'Based in Nairobi, James oversees on-ground operations and government relations across East Africa.' },
            { initials: 'SM', name: 'Sarah Mensah', role: 'Diamond Trade Director', desc: 'Certified diamond grader with 15 years facilitating ethical trade between African producers and global buyers.' },
            { initials: 'VR', name: 'Vikram Rao', role: 'VP, Renewable Energy', desc: 'Renewable energy specialist with $2B+ in African solar and wind projects under his belt.' },
            { initials: 'LN', name: 'Leila Nkosi', role: 'Eco Tourism Lead', desc: 'Conservation biologist and tourism developer who has built award-winning lodges across East Africa.' },
            { initials: 'PS', name: 'Priya Sharma', role: 'CFO', desc: 'CFA with expertise in project finance, cross-border investment structuring, and African capital markets.' },
            { initials: 'EM', name: 'Emmanuel Moyo', role: 'Head, Legal & Compliance', desc: 'Specialist in African mining law, diamond regulations, and cross-jurisdictional contract management.' },
          ].map(m => (
            <div key={m.name} className="team-card">
              <div className="team-avatar">{m.initials}</div>
              <div className="team-info">
                <h4>{m.name}</h4>
                <div className="team-role">{m.role}</div>
                <p>{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Partners = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero">
      <p className="section-label">Our Network</p>
      <h1>Partners & Associates</h1>
      <p>A global network of strategic alliances powering our delivery.</p>
    </div>
    <section className="section">
      <div className="container" style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <p className="section-label">Strategic Partners</p>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Our Global Network</h2>
        <div className="gold-line center" />
        <p className="section-sub center">We collaborate with world-class institutions across finance, technology, environment, and government sectors.</p>
      </div>
      <div className="partner-grid" style={{ maxWidth: '1000px', margin: '0 auto 4rem' }}>
        {[['🏦', 'African Development Bank'], ['🌐', 'World Bank Group'], ['◆', 'De Beers Group'], ['☀️', 'IRENA Africa'], ['🦁', 'African Wildlife Foundation'], ['⚖️', 'Kimberley Process Secretariat'], ['🏭', 'Anglo American'], ['🌿', 'WWF Africa'], ['✈️', 'African Tourism Board'], ['📊', 'IMF Africa Desk']].map(([icon, name]) => (
          <div key={name} className="partner-cell">
            <div className="partner-icon">{icon}</div>
            <p>{name}</p>
          </div>
        ))}
      </div>
      <div style={{ background: '#0d3d2a', padding: '4rem 2rem', textAlign: 'center' }}>
        <h2 className="section-title white" style={{ textAlign: 'center' }}>Become a Partner</h2>
        <div className="gold-line center" />
        <p className="section-sub white center" style={{ marginBottom: '2rem' }}>Join our network and gain access to Africa's most promising investment opportunities.</p>
        <button className="btn-gold" onClick={() => onNavigate('contact')}>Contact Us</button>
      </div>
    </section>
  </PageWrapper>
);
